<template>
  <v-container class="fill-height">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-card class="pa-6" elevation="3">
          <v-card-title class="text-h5 text-center mb-4">
            🎙️ Laboratorio de Voz
          </v-card-title>

          <v-alert
            v-if="isRecording"
            type="info"
            variant="tonal"
            class="mb-4"
            icon="mdi-microphone"
          >
            Escuchando... hable ahora.
          </v-alert>

          <v-divider class="mb-6"></v-divider>

          <v-row justify="center" class="mb-6">
            <v-btn
              :color="isRecording ? 'red' : 'primary'"
              size="large"
              icon
              @click="isRecording ? stopAll() : startAll()"
              class="mx-2"
            >
              <v-icon>{{ isRecording ? 'mdi-stop' : 'mdi-microphone' }}</v-icon>
            </v-btn>

            <v-btn
              :disabled="!audioUrl || isRecording"
              color="success"
              variant="outlined"
              size="large"
              icon
              @click="playAudio"
              class="mx-2"
            >
              <v-icon>mdi-play</v-icon>
            </v-btn>
            <v-btn
            :disabled="!transcription"
            color="secondary"
            variant="elevated"
            icon
            @click="speakText(transcription)"
            class="mx-2"
          >
            <v-icon>mdi-volume-high</v-icon>
          </v-btn>
          </v-row>

          <v-textarea
            v-model="transcription"
            label="Transcripción (Texto)"
            variant="outlined"
            placeholder="Lo que digas aparecerá aquí..."
            readonly
            rows="4"
            auto-grow
            bg-color="grey-lighten-4"
          ></v-textarea>

          <div v-if="audioUrl" class="mt-4">
            <p class="text-caption mb-1">Última grabación:</p>
            <audio ref="audioPlayer" :src="audioUrl" controls class="w-100"></audio>
          </div>

          <v-card-actions class="mt-4">
            <v-spacer></v-spacer>
            <v-btn 
              variant="text" 
              color="grey" 
              @click="clearAll" 
              :disabled="isRecording"
            >
              Limpiar todo
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref, onBeforeUnmount } from 'vue';


const speakText = (text: string) => {
  if (!window.speechSynthesis) {
    alert("Tu navegador no soporta la reproducción de voz.");
    return;
  }

  // Crear el objeto de locución
  const utterance = new SpeechSynthesisUtterance(text);
  
  // Configuración opcional
  utterance.lang = 'es-ES'; // Idioma
  utterance.pitch = 1;      // Tono (0 a 2)
  utterance.rate = 1;       // Velocidad (0.1 a 10)
  utterance.volume = 1;     // Volumen (0 a 1)

  // Opcional: Elegir una voz específica disponible en el sistema
  const voices = window.speechSynthesis.getVoices();
  // Por ejemplo, buscar una voz que suene más natural si existe
  const spanishVoice = voices.find(v => v.lang.includes('es'));
  if (spanishVoice) utterance.voice = spanishVoice;

  // ¡Hablar!
  window.speechSynthesis.speak(utterance);
};

// --- ESTADOS ---
const isRecording = ref(false);
const transcription = ref('');
const audioUrl = ref<string | null>(null);
const audioPlayer = ref<HTMLAudioElement | null>(null);

// --- VARIABLES DE NAVEGADOR ---
let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let recognition: any = null;

// --- CONFIGURACIÓN DE SPEECH RECOGNITION (Voz a Texto) ---
const setupSpeechRecognition = () => {
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  
  if (!SpeechRecognition) {
    alert("Tu navegador no soporta el reconocimiento de voz.");
    return;
  }

  recognition = new SpeechRecognition();
  recognition.lang = 'es-ES'; // Idioma
  recognition.continuous = true;
  recognition.interimResults = true;

  recognition.onresult = (event: any) => {
    let interimTranscript = '';
    for (let i = event.resultIndex; i < event.results.length; ++i) {
      if (event.results[i].isFinal) {
        transcription.value += event.results[i][0].transcript + ' ';
      } else {
        interimTranscript += event.results[i][0].transcript;
      }
    }
  };
};

// --- LOGICA DE GRABACIÓN ---
const startAll = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
    // 1. Iniciar Grabación de Audio
    mediaRecorder = new MediaRecorder(stream);
    audioChunks = [];
    
    mediaRecorder.ondataavailable = (e) => audioChunks.push(e.data);
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      audioUrl.value = URL.createObjectURL(audioBlob);
    };

    mediaRecorder.start();

    // 2. Iniciar Reconocimiento de Voz
    if (!recognition) setupSpeechRecognition();
    recognition.start();

    isRecording.value = true;
  } catch (err) {
    console.error("Error al acceder al micrófono:", err);
  }
};

const stopAll = () => {
  mediaRecorder?.stop();
  recognition?.stop();
  isRecording.value = false;
};

const playAudio = () => {
  audioPlayer.value?.play();
};

const clearAll = () => {
  transcription.value = '';
  audioUrl.value = null;
  audioChunks = [];
};

// Limpiar recursos al destruir componente
onBeforeUnmount(() => {
  stopAll();
});
</script>

<style scoped>
.w-100 {
  width: 100%;
}
</style>